import { css } from 'lit';

/**
 * Foundation design tokens for the MWI Ecomm Assistant widget.
 *
 * These tokens were initially extracted from the ExRD Figma file (Round 4,
 * `AI-Chat-Assistant_MWI`, key `GcEyTNih9LBFAnfaZBwOne`, `Chatbot_default
 * start` node 8742:13418) on 2026-04-27, and re-verified on 2026-04-28
 * against the "Ready for Development" handoff file
 * (`AI-Chat-Assistant_Dev-Handoff_MWI`, key `DSMVDudtzt2Haob8uI37xQ`,
 * "AI Chat Assistant - Final Designs" section node 5603:16105,
 * `Chatbot_default size` reference panel node 5642:5377). All foundation
 * tokens (colors, typography, spacing, radii, shadows) are identical
 * between the two reads. The full spec — token map, component anatomy,
 * decoupling rationale, and state catalog — lives in
 * `planning/figma/TOKENS.md`.
 *
 * NAMING: All tokens use the `--mwi-assistant-*` namespace to:
 *  1. Prevent collision with anything the host page (Sitecore CD) may define.
 *  2. Allow hosts to opt into theming by setting these on any ancestor.
 *  3. Stay specific in case multiple assistant-style widgets coexist on a page.
 *
 * CONSUMPTION: Apply via `static styles = [tokensStyles, componentStyles]` on
 * the root LitElement. The `:host` block here cascades through Shadow DOM
 * boundaries by virtue of CSS custom property inheritance, so child components
 * only need to read tokens — they never redefine them.
 *
 * DECOUPLING: The Figma source is built on Cencora Design System (CDS) tokens,
 * but MWI is moving away from CDS. These tokens are captured as raw primitives
 * — there is no runtime CDS dependency. CDS variable names are referenced in
 * `planning/figma/TOKENS.md` as archaeology only.
 */
export const tokensStyles = css`
  :host {
    /* === Colors ===================================================== */
    --mwi-assistant-color-bg-header: #211359;
    --mwi-assistant-color-bg-body: #ffffff;
    --mwi-assistant-color-bg-footer: #ffffff;
    --mwi-assistant-color-bg-bubble-assistant: #f5f5f5;
    /* User message bubble background. Provisional — neither Round 4 nor
       the Dev-Handoff "Final Designs" file gives the user side its own
       bubble (the Dev-Handoff conversation states render the user input
       as a "From: …" text label above the assistant bubble). See
       TOKENS.md §"User message bubble" / Open question #4. Defaulted to
       the primary interactive purple so user turns are visually distinct
       from the assistant's neutral bubble; subject to Liana's review
       during P4-001 reconciliation. */
    --mwi-assistant-color-bg-bubble-user: #461e96;
    --mwi-assistant-color-bg-input: #ffffff;

    --mwi-assistant-color-interactive-primary: #461e96;
    --mwi-assistant-color-interactive-secondary: #8a8a8a;
    /* Card-border grey — used by the negative-feedback inline reason
       card (P3-025a) and its Dismiss button outline. The hex matches
       the CDS color/neutral/200 shade. */
    --mwi-assistant-color-border-card: #cacaca;
    /* Disabled-button background grey — used by the negative-feedback
       Submit button while no reason is selected (P3-025a). Same hex as
       --mwi-assistant-color-border-card today, but kept as a separate
       semantic token because future theming (e.g. raising contrast for
       the disabled state) may diverge them without re-pointing every
       consumer. */
    --mwi-assistant-color-interactive-disabled: #cacaca;

    --mwi-assistant-color-text-primary: #3b3b3b;
    --mwi-assistant-color-text-on-primary: #ffffff;
    --mwi-assistant-color-text-placeholder: #6b6b6b;
    /* Muted text color for placeholder/system messages inside a bubble
       (e.g. the "(assistant response will appear once API is wired up)"
       seam used by P1-053 until P1-058 lands). Distinct semantic from
       --color-text-placeholder, which is for empty input fields. */
    --mwi-assistant-color-text-muted: #6b6b6b;
    /* Secondary grey for compact label text that sits OUTSIDE a bubble
       — used today by the per-turn 'Helpful response?' feedback prompt
       (P3-023a). Distinct semantic from --color-text-muted (in-bubble
       placeholder copy) and --color-text-placeholder (empty input
       fields). The shade is a touch darker than -muted / -placeholder
       to give the small (12px) feedback prompt label enough contrast
       against the white body background. */
    --mwi-assistant-color-text-secondary: #6e6e6e;

    /* === Typography ================================================= */
    /* Gilroy is licensed; Manrope is the closest free analog and stays in
       the stack until font licensing is resolved. See TOKENS.md §Open
       questions. */
    --mwi-assistant-font-family: 'Gilroy', 'Manrope', system-ui, -apple-system,
      'Segoe UI', Roboto, sans-serif;

    --mwi-assistant-font-heading-lg-size: 18px;
    --mwi-assistant-font-heading-lg-line: 24px;
    --mwi-assistant-font-heading-lg-weight: 600;

    --mwi-assistant-font-body-md-size: 16px;
    --mwi-assistant-font-body-md-line: 24px;
    --mwi-assistant-font-body-md-weight: 700;

    --mwi-assistant-font-body-sm-size: 14px;
    --mwi-assistant-font-body-sm-line: 20px;
    --mwi-assistant-font-body-sm-weight: 500;

    --mwi-assistant-font-body-2xs-size: 12px;
    --mwi-assistant-font-body-2xs-line: 16px;
    --mwi-assistant-font-body-2xs-weight: 500;

    --mwi-assistant-font-button-sm-size: 14px;
    --mwi-assistant-font-button-sm-line: 16px;
    --mwi-assistant-font-button-sm-weight: 700;

    --mwi-assistant-font-paragraph-spacing: 16px;

    /* === Spacing scale ============================================== */
    --mwi-assistant-space-1: 4px;
    --mwi-assistant-space-1-5: 6px;
    --mwi-assistant-space-2: 8px;
    --mwi-assistant-space-3: 12px;
    --mwi-assistant-space-4: 16px;
    --mwi-assistant-space-6: 24px;

    /* === Radii & borders ============================================ */
    --mwi-assistant-radius-sm: 4px;
    --mwi-assistant-radius-md: 8px;
    --mwi-assistant-border-thin: 1px;
    --mwi-assistant-border-thick: 2px;

    /* === Elevation ================================================== */
    --mwi-assistant-shadow-overlay: 0 0 1px rgba(0, 0, 0, 0.4),
      0 2px 4px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.1);
    --mwi-assistant-shadow-header: 0 4px 2.5px rgba(0, 0, 0, 0.25);

    /* === Modal / overlay ============================================ */
    /* Used by the disclaimer modal (P1-051) and any future in-widget
       overlay (close-confirmation in P1-056). Opacity tuned so the
       chat content stays partially visible behind the modal — this is
       a provisional value, subject to Liana's review during P4-001. */
    --mwi-assistant-color-backdrop: rgba(0, 0, 0, 0.45);

    /* === Fixed dimensions =========================================== */
    /* Per Figma. Conflicts with REQUIREMENTS.md §4.1 (380×600); Figma is
       treated as authoritative pending Liana's confirmation during
       P4-001. The Dev-Handoff "Final Designs" file pins these as
       Default=600, Max=740, Min=412 (total widget heights, see
       TOKENS.md §Fixed dimensions). The frame caps at widget-max-height
       (and further at the viewport); the transcript body scrolls inside
       the remaining space between header and footer. See TOKENS.md §Open
       questions. */
    --mwi-assistant-size-width: 530px;
    /* Default expanded-frame height (Figma Chatbot_default size). Applied
       on .frame until the user resizes via header drag (P4-005). Hosts
       may override via --mwi-assistant-size-widget-default-height. */
    --mwi-assistant-size-widget-default-height: 600px;
    /* User-resizable floor (Figma Chatbot_min size). Hosts may override
       via the min-height attribute or this custom property directly. */
    --mwi-assistant-size-widget-min-height: 412px;
    /* Total expanded-frame cap (Figma Chatbot_max). Applied on .frame
       together with a viewport guard — not on .body directly. */
    --mwi-assistant-size-widget-max-height: 740px;
    /* Live frame height set by user resize; falls back to default-height
       when unset. Written by {@link MwiAssistantElement._syncFrameGeometry}. */
    --mwi-assistant-size-widget-height: var(
      --mwi-assistant-size-widget-default-height
    );
    /* Legacy token — hosts may still override this name; the frame layout
       no longer reads it (see widget-max-height above). Kept so existing
       host overrides do not silently disappear. */
    --mwi-assistant-size-body-max-height: 740px;
    --mwi-assistant-size-input-height: 52px;
    /* Maximum height the input row may auto-grow to before the textarea
       inside starts its own internal scroll. Matches REQUIREMENTS.md
       §5.1 ("Maximum input height: 112px (4 lines)"). The 4-line cap is
       computed from --mwi-assistant-font-body-sm-line (20px) × 4 +
       2 × --mwi-assistant-space-1-5 (16px) ≈ 96–112px depending on
       inherited line-height; 112px is the spec value. */
    --mwi-assistant-size-input-max-height: 112px;
    --mwi-assistant-size-button-height: 32px;
    --mwi-assistant-size-icon-md: 24px;
    --mwi-assistant-size-icon-sm: 15px;

    /* Launcher (P1-055) — floating circular re-open CTA shown when the
       widget is minimized or closed. Provisional dimension; the
       production launcher lives in the host nav bar (P4-006) and its
       final visual treatment is an open question in TOKENS.md
       §Components not yet captured. */
    --mwi-assistant-size-launcher: 56px;
  }
`;
